/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type React from 'react';
import {useEffect, useState} from 'react';
import {Form, Tab, Tabs, Button, Spinner} from 'react-bootstrap';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import {toast} from 'react-toastify';

import SessionTableGx from 'components/Tables/SessionsTableGx';
import SessionTableRx from 'components/Tables/SessionsTableRx';
import LayoutMain from 'components/Layouts/LayoutMain';
import {
	// gx sessions
	useLazyGetGxDnnQuery,
	useLazyGetGxSessionIpv4AddressQuery,
	useLazyGetGxSessionIpv6AddressQuery,
	useLazyGetGxSupiQuery,
	useLazyGetGxGpsiQuery,
	useLazyGetGxImeiQuery,
	useTerminateGxSessionByIdMutation,
	// rx sessions
	useLazyGetRxDnnQuery,
	useLazyGetRxSessionIpv4AddressQuery,
	useLazyGetRxSessionIpv6AddressQuery,
	useLazyGetRxSupiQuery,
	useLazyGetRxGpsiQuery,
	useAbortRxSessionByIdMutation,
} from 'services';
import type {GxSession} from 'interfaces';

TimeAgo.setDefaultLocale(en.locale);
TimeAgo.addLocale(en);

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles.css';

type TProps = {
	title: string;
	active: string;
	isLoading?: boolean;
};

type TData = {
	type: string;
	valueOfFind: string;
	dataGx: GxSession[] | unknown;
	dataRx: GxSession[] | unknown;
};

const TIME_INTERVAL = 59000;

const RenderTabTitle = ({title, active, isLoading = false}: TProps) => (
	<Button
		variant="outline-dark"
		style={{
			border: 'none',
			background: 'transparent',
			color: title.includes(active) ? '#212529' : '#9e9e9e',
		}}>
		{isLoading && (
			<Spinner
				as="span"
				animation="border"
				size="sm"
				role="status"
				aria-hidden="true"
				className="mr-2"
			/>
		)}
		{title}
	</Button>
);

const SessionDataQuery = () => {
	// api gx sessions
	const [getGxSessionIpv4Address, {isLoading: isLoadingIpv4}] =
		useLazyGetGxSessionIpv4AddressQuery();
	const [getGxSessionIpv6Address, {isLoading: isLoadingIpv6}] =
		useLazyGetGxSessionIpv6AddressQuery();
	const [getGxSupi, {isLoading: isLoadingSupi}] = useLazyGetGxSupiQuery();
	const [getGxDnn, {isLoading: isLoadingDnn}] = useLazyGetGxDnnQuery();
	const [getGxGpsi, {isLoading: isLoadingGpsi}] = useLazyGetGxGpsiQuery();
	const [getGxImei, {isLoading: isLoadingImei}] = useLazyGetGxImeiQuery();

	// api rx sessions
	const [getRxSessionIpv4Address, {isLoading: isLoadingIpv4Rx}] =
		useLazyGetRxSessionIpv4AddressQuery();
	const [getRxSessionIpv6Address, {isLoading: isLoadingIpv6Rx}] =
		useLazyGetRxSessionIpv6AddressQuery();
	const [getRxSupi, {isLoading: isLoadingSupiRx}] = useLazyGetRxSupiQuery();
	const [getRxDnn, {isLoading: isLoadingDnnRx}] = useLazyGetRxDnnQuery();
	const [getRxGpsi, {isLoading: isLoadingGpsiRx}] = useLazyGetRxGpsiQuery();

	// remove session
	const [terminateGxSessionsById] = useTerminateGxSessionByIdMutation();
	const [abortRxSessionById] = useAbortRxSessionByIdMutation();

	const [initialDate, setInitialDate] = useState<Date>(new Date());
	const [timeStr, setTimeStr] = useState<string>('just now');
	const [key, setKey] = useState<string>('GX Session');
	const [dataSession, setDataSession] = useState<TData>({
		type: '',
		valueOfFind: '',
		dataGx: [],
		dataRx: [],
	});
	const timeAgo = new TimeAgo('en-US');

	const changeImsi = (e: React.ChangeEvent<HTMLInputElement>) =>
		setDataSession({...dataSession, type: e.target.value});

	const changeValueOfFind = (e: React.ChangeEvent<HTMLInputElement>) =>
		setDataSession({...dataSession, valueOfFind: e.target.value});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (dataSession.type === '' || dataSession.valueOfFind === '') {
			toast.warning('Please review fields');
			return;
		}

		let datasGx: GxSession;
		let datasRx: GxSession;
		switch (dataSession.type) {
			case 'Ipv4Address':
				datasGx = await getGxSessionIpv4Address(
					dataSession.valueOfFind,
				).unwrap();
				datasRx = await getRxSessionIpv4Address(
					dataSession.valueOfFind,
				).unwrap();
				break;
			case 'Ipv6Address':
				datasGx = await getGxSessionIpv6Address(
					dataSession.valueOfFind,
				).unwrap();
				datasRx = await getRxSessionIpv6Address(
					dataSession.valueOfFind,
				).unwrap();
				break;
			case 'SUPI':
				datasGx = await getGxSupi(dataSession.valueOfFind).unwrap();
				datasRx = await getRxSupi(dataSession.valueOfFind).unwrap();
				break;
			case 'DNN':
				datasGx = await getGxDnn(dataSession.valueOfFind).unwrap();
				datasRx = await getRxDnn(dataSession.valueOfFind).unwrap();
				break;
			case 'GPSI':
				datasGx = await getGxGpsi(dataSession.valueOfFind).unwrap();
				datasRx = await getRxGpsi(dataSession.valueOfFind).unwrap();
				break;
			case 'IMEI':
				datasGx = await getGxImei(dataSession.valueOfFind).unwrap();
				break;
			default:
				break;
		}
		setDataSession(prev => ({...prev, dataGx: datasGx, dataRx: datasRx}));
	};

	const handleDateAgo = () => setTimeStr(timeAgo.format(initialDate));

	/* A custom hook that is used to update the time every 59 seconds. */
	useEffect(() => {
		const interval = setInterval(() => {
			handleDateAgo();
		}, TIME_INTERVAL);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* It's a hook that runs after the component is rendered. */
	useEffect(() => {
		setInitialDate(new Date());
		handleDateAgo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataSession.dataGx, dataSession.dataRx]);

	return (
		<LayoutMain>
			<div className="container-fluid" data-testid="session">
				{/* Intro */}
				<div className="row">
					<div className="col-md-12">
						<h1 className="mt-2">Session Data Query</h1>
					</div>
				</div>
				{/* Session Data Query*/}
				<div className="row mt-4">
					<div className="col-md-12">
						<div className="card-tasks card">
							<div className="card-header">
								<i className="bi bi-server mr-3 float-left text-secondary bi-2" />
								<h2 className="card-title">Session Data Query</h2>
								<p className="card-category">
									Access to the Gx and Rx active sessions running in the PCRF
								</p>
							</div>
							<div className="card-body">
								<Form onSubmit={handleSubmit}>
									<div className="row">
										<div className="col-md-3 mb-3">
											<label htmlFor="type">Type</label>

											<Form.Control
												defaultValue={''}
												as="select"
												onChange={changeImsi}
												className="custom-select d-block w-100">
												<option value="">Choose...</option>
												<option value="SUPI">SUPI</option>
												<option value="Ipv4Address">IP v4</option>
												<option value="Ipv6Address">IP v6</option>
												<option value="GPSI">GPSI</option>
												<option value="DNN">DNN</option>
												<option value="IMEI">IMEI</option>
											</Form.Control>
										</div>
										<div className="col-md-3 mb-3">
											<label htmlFor="search">Search</label>
											<div className="input-group">
												<input
													type="text"
													className="form-control"
													placeholder="Search"
													name="search"
													defaultValue={''}
													onChange={e => changeValueOfFind(e)}
												/>
												<div className="input-group-btn">
													<button className="btn btn-primary" type="submit">
														<i className="bi-1 bi-search" />
													</button>
												</div>
											</div>
											<div className="invalid-feedback">
												Please provide a valid state.
											</div>
										</div>
									</div>
								</Form>
								<div className="tabs-container mt-4">
									<Tabs
										style={{
											height: '50px',
											/* color: '#495057',
											backgroundColor: '#fff',
											borderColor: '#0884ff #007fff #fff', */
										}}
										activeKey={key}
										// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
										onSelect={k => setKey(k!)}
										// defaultActiveKey="gxSession"
										id="uncontrolled-tab-example">
										<Tab
											eventKey="GX Session"
											title={
												<RenderTabTitle
													active={key}
													isLoading={
														isLoadingIpv4 ||
														isLoadingIpv6 ||
														isLoadingSupi ||
														isLoadingDnn ||
														isLoadingGpsi ||
														isLoadingImei
													}
													title="GX Session"
												/>
											}>
											<SessionTableGx
												title="GxSession"
												dataSession={dataSession.dataGx}
												deleteSession={terminateGxSessionsById}
											/>{' '}
										</Tab>
										<Tab
											eventKey="RX Session"
											title={
												<RenderTabTitle
													active={key}
													isLoading={
														isLoadingIpv4Rx ||
														isLoadingIpv6Rx ||
														isLoadingSupiRx ||
														isLoadingDnnRx ||
														isLoadingGpsiRx
													}
													title="RX Session"
												/>
											}>
											<SessionTableRx
												title="RxSession"
												dataSession={dataSession.dataRx}
												deleteSession={abortRxSessionById}
											/>{' '}
										</Tab>
									</Tabs>
								</div>
							</div>
							<div className="card-footer">
								<hr />
								<div className="stats">
									<i className="bi-1 bi-clock-history" /> Updated {timeStr}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LayoutMain>
	);
};

export default SessionDataQuery;
